import { useEffect, useMemo, useRef } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowDownRight, ArrowUpRight, PieChart, Wallet } from 'lucide-react';
import { createApiClient } from '../api/client';
import { useAccessToken } from '../auth/useAuth';
import { useWorkspace } from '../workspace/WorkspaceProvider';
import { greetingName } from '../workspace/userName';
import { NameNudge } from '../components/NameNudge';
import { AdvisorLandingNudge } from '../advisor/AdvisorLandingNudge';
import { OnboardingRequestCard } from '../onboardingrequest/OnboardingRequestCard';
import { OnboardingInvitationCard } from '../onboarding/client/OnboardingInvitationCard';
import { GettingStartedCard } from '../components/GettingStartedCard';
import { WelcomeOverlay } from '../firstrun/WelcomeOverlay';
import { OfflineState, QueryErrorState } from '../components/QueryErrorState';
import { queryDisplayState } from '../lib/queryState';
import {
  Resource,
  AccountAttributes, AssetAttributes, LiabilityAttributes,
  NetWorthResponse, NetWorthHistoryResponse, AdvisorConnection,
} from '../api/types';
import { useIsAdmin } from '../lib/useIsAdmin';
import { attentionItems, withCashFlowQuestions, type AttentionDocumentSource } from '../lib/attention';
import { assetGroupFor, ASSET_GROUP_ORDER, type AssetGroup } from '../lib/assetClass';
import { useFirstRun, type FirstRunCounts } from '../lib/firstRun';
import { advisorConnectionsQueryOptions } from '../advisor/useAdvisorConnections';
import { useCanWrite } from '../lib/useCanWrite';
import { useDashboardSummary } from '../lib/useDashboardSummary';
import { windowNetWorthHistory } from '../lib/netWorthTrend';
import { fmtDate } from '../lib/formatDate';
import { useBrand } from '../brand/BrandProvider';
import { useTheme } from '../theme/ThemeProvider';
import { CompositionBar, CompositionLegend, allocationColors, type CompositionSlice } from '../components/CompositionBar';
import { currencyCode, formatCompactMoney, statementCurrency } from '../lib/currency';
import { UserText } from '../components/UserText';
import { NeedsAttentionList } from '../components/NeedsAttentionList';
import { NetCashFlowCard, useCashFlowQuestions } from './cashFlow/NetCashFlowCard';
import { workspaceKey } from '../lib/workspaceQueryKey';

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

function todayStr() {
  return new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
}

function formatCurrency(n: number, currency = 'USD', fractionDigits = 2) {
  // Guarded: a stored currency of '' or 'US' reaches Intl unchanged otherwise, and Intl
  // throws a RangeError on anything that is not a well-formed three-letter code — during
  // render, which blanks the landing page. Falling back to USD degrades this one figure's
  // formatting instead of crashing the page (steward-app#479).
  const code = currencyCode(currency) ?? 'USD';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: code, minimumFractionDigits: fractionDigits, maximumFractionDigits: fractionDigits }).format(n);
}

/** Range for the hero trend chip — the past-month delta reads best on a home surface. */
type TrendRange = '1M' | 'YTD' | '1Y';
const TREND_LABEL: Record<TrendRange, string> = { '1M': 'past month', 'YTD': 'year to date', '1Y': 'past year' };

/**
 * Minimal sparkline (no axes/labels) for the Net Worth hero — a bespoke SVG
 * rather than the full TimeSeriesChart, since a hero wants the shape only, not a
 * readable plot. Renders as a full-width band beneath the number so the line
 * terminates under the figure (reads as one unit, not a floating shape). The
 * line simply ends at the current value (no endpoint marker — the terminus is
 * "today" by construction). Renders nothing below two points. Always the brand
 * bronze — the same color the Portfolio performance chart uses, so the net-worth
 * line reads as one consistent thing across both surfaces; direction is carried by
 * the trend chip + the line's own shape, not by recoloring it.
 */
function Sparkline({ values }: { values: number[] }) {
  if (values.length < 2) return null;
  const W = 600, H = 56, pad = 4;
  const min = Math.min(...values), max = Math.max(...values);
  const span = max - min || 1;
  const stepX = (W - pad * 2) / (values.length - 1);
  const pts = values.map((v, i) => {
    const x = pad + i * stepX;
    const y = pad + (H - pad * 2) * (1 - (v - min) / span);
    return [x, y] as const;
  });
  const line = pts.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  const area = `${line} L${pts[pts.length - 1][0].toFixed(1)},${H} L${pts[0][0].toFixed(1)},${H} Z`;
  const stroke = 'var(--color-bronze)';
  const gradId = 'spark-nw';
  return (
    // Grows to fill (steward-app#2827): when the Asset Allocation card beside it is taller, the
    // line takes the extra height rather than leaving a gap above it (#2509 item 6). The drawing
    // stretches vertically (preserveAspectRatio none, non-scaling stroke); 56px is its floor.
    <div data-testid="net-worth-sparkline" className="relative w-full flex-1" style={{ minHeight: H }} aria-hidden>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="absolute inset-0 block h-full w-full overflow-visible">
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={stroke} stopOpacity="0.16" />
            <stop offset="100%" stopColor={stroke} stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={area} fill={`url(#${gradId})`} />
        <path d={line} fill="none" stroke={stroke} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
      </svg>
    </div>
  );
}

/**
 * Net Worth hero — the home surface's headline. Big live number (same math as the
 * Portfolio statement and the old tile), a trend chip vs. recorded snapshots, and
 * a sparkline of the net-worth history. Trend + sparkline hide gracefully until
 * the first nightly snapshot exists (new workspaces), leaving just the number.
 */
function NetWorthHero({
  netWorth, totalAssets, totalLiabilities, currency, loading,
  change, history, range, to,
}: {
  netWorth: number;
  totalAssets: number;
  totalLiabilities: number;
  currency: string;
  loading: boolean;
  change: {
    amount: number; percent: number;
    truncated?: boolean; overExtended?: true; insufficientHistory?: true; anchorDate?: string;
  } | null;
  history: number[];
  range: TrendRange;
  /** Where the card opens: a stretched link on the eyebrow, so it is keyboard-operable. */
  to: string;
}) {
  // Trend direction drives the sparkline color. Prefer the server change delta;
  // when it's absent (e.g. /net-worth still loading or errored) fall back to the
  // history series' own first→last direction so a declining line isn't painted "up".
  const up = change
    ? change.amount >= 0
    : history.length >= 2 ? history[history.length - 1] >= history[0] : true;
  // A workspace with no anchor at all has no real figure to show (steward-app#400) —
  // suppress the chip entirely rather than render its `{0, 0}` wire value.
  const showChip = !!change && !change.insufficientHistory;
  // A truncated OR over-extended range doesn't cover what its name claims, in either
  // direction, so name the anchor instead. Captioning a three-month move "past month"
  // would be a smaller lie than the +41,900% this whole feature exists to stop, but it
  // would still be one. `anchorDate` always comes from the server now (steward-app#400);
  // `historyStart` is no longer threaded here for this purpose.
  const trendLabel = (change?.truncated || change?.overExtended) && change.anchorDate
    ? `since ${fmtDate(change.anchorDate)}`
    : TREND_LABEL[range];
  return (
    <div
      data-tour="net-worth"
      className="relative flex flex-col rounded-token border border-line bg-surface p-6 sm:p-7 shadow-panel transition-[box-shadow] duration-150 hover:shadow-panel-hover"
    >
      <div className="flex flex-1 flex-col">
        <div className="mb-2 flex items-center gap-2">
          <Link to={to} className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate font-sans after:content-[''] after:absolute after:inset-0 after:rounded-token no-underline focus:outline-none focus-visible:after:ring-2 focus-visible:after:ring-bronze-strong focus-visible:after:ring-offset-2">Net Worth</Link>
          <Wallet size={14} strokeWidth={1.75} aria-hidden className="text-bronze opacity-70" />
        </div>
        {loading ? (
          <div className="skeleton h-11 w-56 mb-2" />
        ) : (
          <div className="font-serif text-[34px] sm:text-[44px] font-normal leading-none text-navy tracking-[-0.01em]">
            {formatCurrency(netWorth, currency, 0)}
          </div>
        )}
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] font-sans">
          {showChip && change && (
            <span className={`inline-flex items-center gap-1 font-semibold ${up ? 'text-navy' : 'text-slate'}`}>
              {up
                ? <ArrowUpRight size={13} strokeWidth={2.25} aria-hidden className="text-bronze" />
                : <ArrowDownRight size={13} strokeWidth={2.25} aria-hidden />}
              {up ? '+' : ''}{formatCompactMoney(change.amount, currency)} ({up ? '+' : ''}{change.percent.toFixed(1)}%)
              <span className="font-medium text-slate">{trendLabel}</span>
            </span>
          )}
          <span className="font-medium text-slate">
            Assets {formatCurrency(totalAssets, currency, 0)} · Liabilities {formatCurrency(totalLiabilities, currency, 0)}
          </span>
        </div>
        {/* Floor: the line terminates under the number, so hero + trend + chart
            read as one figure. flex-1 lets it take whatever height the row gives
            the card, so a taller sibling widget grows the chart, not a gap. */}
        {history.length >= 2 && (
          <div className="flex flex-1 flex-col pt-6">
            <Sparkline values={history} />
          </div>
        )}
      </div>
    </div>
  );
}

// ── Asset allocation ────────────────────────────────────────────────────────
// Segment colours live in components/CompositionBar (shared with the firm Overview).
const ALLOC_MAX = 6; // top N groups, remainder folds into "Other"

interface AllocSlice { label: string; value: number; pct: number; color: string; }

function buildAllocation(assets: Resource<AssetAttributes>[], colors: string[]): { slices: AllocSlice[]; total: number } {
  const byGroup = new Map<AssetGroup, number>();
  for (const a of assets) {
    const v = a.attributes.currentValue ?? 0;
    if (v <= 0) continue;
    const g = assetGroupFor(a.attributes.assetClass);
    byGroup.set(g, (byGroup.get(g) ?? 0) + v);
  }
  const total = [...byGroup.values()].reduce((s, v) => s + v, 0);
  if (total <= 0) return { slices: [], total: 0 };

  const ranked = [...byGroup.entries()]
    .map(([label, value]) => ({ label: label as string, value }))
    .sort((a, b) => b.value - a.value || ASSET_GROUP_ORDER.indexOf(a.label as AssetGroup) - ASSET_GROUP_ORDER.indexOf(b.label as AssetGroup));

  // Fold everything past the top N into "Other" — merging into a genuine "Other"
  // group (assetGroupFor's catch-all) if it already ranked into the head, so the
  // bucket is never rendered twice (duplicate label ⇒ duplicate React key + legend).
  const head = ranked.slice(0, ALLOC_MAX);
  const tail = ranked.slice(ALLOC_MAX);
  const tailSum = tail.reduce((s, r) => s + r.value, 0);
  const otherIdx = head.findIndex(h => h.label === 'Other');
  const merged = !tail.length
    ? head
    : otherIdx >= 0
      ? head.map((h, i) => (i === otherIdx ? { ...h, value: h.value + tailSum } : h))
      : [...head, { label: 'Other', value: tailSum }];

  const slices = merged.map((s, i) => ({
    ...s,
    pct: (s.value / total) * 100,
    color: colors[Math.min(i, colors.length - 1)],
  }));
  return { slices, total };
}

/**
 * Account types that mean "inside a retirement wrapper", case-folded (steward-app#1028).
 *
 * Two writers put a string in `AccountAttributes.accountType` and they do not share a
 * vocabulary, which is why Accounts.tsx already folds case before grouping on it:
 *  - the account form writes a display string from ACCOUNT_TYPES, so a hand-entered
 *    retirement account is 'Retirement';
 *  - account-sync writes Plaid's `subtype` lowercased (`mapAccountType` in
 *    packages/account-aggregation), so a synced IRA is 'ira', a synced 401(k) is '401k',
 *    and neither is ever 'Retirement'. Matching the form's word alone would tell a family
 *    whose synced 401(k) is the largest thing they own that they hold $0 in retirement.
 *
 * The Plaid entries are the retirement wrappers in Plaid's account `subtype` enum as read
 * from plaid.com/docs/api/accounts on 2026-09-07. Left out on purpose: 'tfsa', 'rdsp' and
 * 'resp' (general, disability and education savings, which Plaid files beside retirement
 * and a family would not), and every non-wrapper type including 'trust' and 'other'. A
 * subtype Plaid adds later is missed until this list learns it, which undercounts the
 * line rather than overcounting it. The behaviour in both directions is pinned by
 * DashboardAllocationTotals.test.tsx, not by this comment.
 */
const RETIREMENT_ACCOUNT_TYPES: ReadonlySet<string> = new Set([
  'retirement',
  '401a', '401k', '403b', '457b', 'ira', 'keogh', 'pension', 'profit sharing plan',
  'roth', 'roth 401k', 'roth 403b', 'roth 457b', 'roth pension', 'roth profit sharing plan',
  'roth thrift savings plan', 'sarsep', 'sep ira', 'simple ira', 'thrift savings plan',
  'lif', 'lira', 'lrif', 'lrsp', 'prif', 'rlif', 'rrif', 'rrsp',
]);

function isRetirementAccountType(accountType: string | undefined): boolean {
  return RETIREMENT_ACCOUNT_TYPES.has((accountType ?? '').trim().toLowerCase());
}

/**
 * The value held inside retirement accounts, on the same positive-only basis as
 * buildAllocation(), so it is a subset of that bar's total and reads honestly as an
 * "of which" beneath it rather than as a further slice: a retirement holding is already
 * counted in whichever asset group it belongs to, and a bar with a Retirement segment
 * would count it twice and stop summing to total assets.
 *
 * Resolved through each position's `accountId`, the same key Portfolio's `accountFor`
 * follows, via the one account Map this page already builds for the largest-holdings
 * list (a Map keeps the last row for a repeated id where `find` would keep the first; ids
 * are unique in the data model, so the two agree). A direct holding (no accountId) or one
 * whose account no longer resolves cannot be inside a retirement wrapper and contributes
 * nothing.
 */
function retirementHeld(
  assets: Resource<AssetAttributes>[],
  accountById: Map<string, Resource<AccountAttributes>>,
): number {
  let sum = 0;
  for (const a of assets) {
    const v = a.attributes.currentValue ?? 0;
    if (v <= 0 || !a.attributes.accountId) continue;
    if (isRetirementAccountType(accountById.get(a.attributes.accountId)?.attributes.accountType)) sum += v;
  }
  return sum;
}

/** Asset-allocation widget: a 100% composition bar + legend, so the *shape* of the
 *  family's wealth reads at a glance. Sits beside the Net Worth hero as a matched
 *  card (same surface + eyebrow). Renders nothing when there are no valued assets —
 *  the parent then lets the hero span the full row. */
function AllocationStrip({ assets, retirement, currency, loading, theme, to }: {
  assets: Resource<AssetAttributes>[];
  /** Value inside retirement accounts, from retirementHeld(); a subset of the bar's total. */
  retirement: number;
  currency: string;
  loading: boolean;
  theme: 'light' | 'dark';
  /** Where the card opens: a stretched link on the eyebrow, so it is keyboard-operable. */
  to: string;
}) {
  const cardClass = 'flex flex-col rounded-token border border-line bg-surface p-6 sm:p-7 shadow-panel';
  const { slices, total } = buildAllocation(assets, allocationColors(theme));
  const barSlices: CompositionSlice[] = slices.map(s => ({
    label: s.label,
    pct: s.pct,
    color: s.color,
    valueText: formatCompactMoney(s.value, currency),
    title: `${s.label}: ${formatCurrency(s.value, currency, 0)} (${s.pct.toFixed(1)}%)`,
  }));
  if (loading) {
    return (
      <div className={cardClass}>
        <div className="skeleton h-3 w-28 mb-6" />
        <div className="skeleton h-3.5 w-full rounded-full mb-5" />
        <div className="skeleton h-16 w-full" />
      </div>
    );
  }
  if (slices.length === 0) return null;

  return (
    <div
      className={`${cardClass} relative transition-[box-shadow] duration-150 hover:shadow-panel-hover`}
    >
      <div className="mb-5 flex items-center gap-2">
        <Link to={to} className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate font-sans after:content-[''] after:absolute after:inset-0 after:rounded-token no-underline focus:outline-none focus-visible:after:ring-2 focus-visible:after:ring-bronze-strong focus-visible:after:ring-offset-2">Asset Allocation</Link>
        <PieChart size={14} strokeWidth={1.75} aria-hidden className="text-bronze opacity-70" />
      </div>
      {/* Bar + legend centered in the remaining height, so the widget fills the
          row without a lopsided gap when the hero card is the taller sibling. */}
      <div className="flex flex-1 flex-col justify-center">
        {/* Composition bar: 2px surface gaps between fills (shared CompositionBar). */}
        <CompositionBar slices={barSlices} ariaLabel={`Asset allocation: ${slices.map(s => `${s.label} ${Math.round(s.pct)}%`).join(', ')}`} />
        {/* Legend — carries identity (name, amount, %) so the bar is never color-alone and
            the amount is on screen, not only in the segment's hover title (steward-app#1028:
            a title tooltip is unreachable on touch). Columns follow the card's width, not
            the viewport's: one column on a phone, two once the card is full-width on a
            tablet, back to one when the card becomes half of a two-column row at `lg`, and
            two again only at `2xl`, the first width at which a half-width card's column
            holds the longest group label beside a compact figure without truncating. */}
        <CompositionLegend
          className="mt-5 grid grid-cols-1 gap-x-6 gap-y-2.5 sm:grid-cols-2 lg:grid-cols-1 2xl:grid-cols-2"
          slices={barSlices}
        />
        {/* "Of which": retirement is an account type, not an asset kind, so it overlaps the
            groups above and is stated beneath them rather than drawn as a further slice
            that would stop the bar summing to total assets. Omitted at zero rather than
            shown as $0, because a zero here can also mean an account type this page does
            not recognise (see RETIREMENT_ACCOUNT_TYPES), and silence is more honest than a
            confident nought. */}
        {retirement > 0 && (
          <div className="mt-4 flex items-center gap-2 border-t border-line/60 pt-3 min-w-0">
            {/* Prose, so it wraps where a figure would be cut: the label is the only thing on
                this row that may take two lines, and it does at 375px and in the half-width
                card at `lg`. */}
            <span className="min-w-0 font-sans text-[12px] text-slate">Of which in retirement accounts</span>
            <span className="ml-auto shrink-0 font-sans text-[11px] tabular-nums text-body">{formatCompactMoney(retirement, currency)}</span>
            <span className="w-9 shrink-0 text-right font-sans text-[11px] tabular-nums text-slate">{((retirement / total) * 100).toFixed(0)}%</span>
          </div>
        )}
      </div>
    </div>
  );
}

export function Dashboard() {
  const token = useAccessToken()!;
  const navigate = useNavigate();
  const { profile, activeWorkspaceId, setFirstRunDismissed } = useWorkspace();
  const { brand, productName } = useBrand();
  const { theme } = useTheme();
  // The suite lockup under the greeting: firm brands show their product name;
  // the default shows the endorsed "Crossmarc Steward" pairing.
  const suiteName = brand ? productName : 'Crossmarc Steward';
  const isAdmin = useIsAdmin();
  const canWrite = useCanWrite();
  const [searchParams, setSearchParams] = useSearchParams();
  const firstRunRef = useRef<HTMLDivElement>(null);

  // Counts (documents/entities/contacts/connections/members/invites) and
  // near-expiry documents come from one façade summary request (PS6) instead
  // of six full, unpaginated list fetches — a 2000-document family no longer
  // ships every document to the browser just to render a count and a handful
  // of expiry badges. Assets/accounts/liabilities are still fetched in full
  // below: the hero, allocation strip, and largest-holdings list need their
  // actual per-record values, not just a count.
  const summaryQ = useDashboardSummary();

  const results = useQueries({
    queries: [
      {
        queryKey: workspaceKey('accounts', activeWorkspaceId),
        queryFn: () => createApiClient(token).get<{ items: Resource<AccountAttributes>[] }>('/accounts').then(r => r.items),
        enabled: !!token && !!activeWorkspaceId,
      },
      {
        queryKey: workspaceKey('assets', activeWorkspaceId),
        queryFn: () => createApiClient(token).get<{ items: Resource<AssetAttributes>[] }>('/assets').then(r => r.items),
        enabled: !!token && !!activeWorkspaceId,
      },
      {
        queryKey: workspaceKey('liabilities', activeWorkspaceId),
        queryFn: () => createApiClient(token).get<{ items: Resource<LiabilityAttributes>[] }>('/liabilities').then(r => r.items),
        enabled: !!token && !!activeWorkspaceId,
      },
      // Snapshot-based net worth + change deltas power the hero's trend chip; the
      // history series feeds its sparkline. Both degrade to "no trend" on a
      // fresh workspace (no snapshots yet) — the big number still renders live.
      {
        queryKey: workspaceKey('net-worth', activeWorkspaceId),
        queryFn: () => createApiClient(token).get<NetWorthResponse>('/net-worth'),
        enabled: !!token && !!activeWorkspaceId,
      },
      {
        queryKey: workspaceKey('net-worth-history', activeWorkspaceId),
        // Whole response, not just `.items`: it also carries where the visible history
        // starts, which the hero needs to caption a window the data doesn't fully cover.
        queryFn: () => createApiClient(token).get<NetWorthHistoryResponse>('/net-worth/history'),
        enabled: !!token && !!activeWorkspaceId,
      },
    ],
  });

  const [accountsQ, positionsQ, liabilitiesQ, netWorthQ, historyQ] = results;

  // Memoized rather than plain `?? []` fallbacks: the `currency` useMemo below depends on
  // all three, and an inline `[]` fallback is a fresh array identity every render, which
  // would defeat that memo on any unrelated re-render (a tour step, the welcome overlay).
  const accounts    = useMemo(() => accountsQ.data    ?? [], [accountsQ.data]);
  const positions   = useMemo(() => positionsQ.data   ?? [], [positionsQ.data]);
  const liabilities = useMemo(() => liabilitiesQ.data ?? [], [liabilitiesQ.data]);
  const counts      = summaryQ.data?.counts;

  // Live, same math as the Portfolio statement (sum of server-enriched
  // currentValue minus liability balances) — the two always agree.
  const totalAssets      = positions.reduce((sum, p) => sum + (p.attributes.currentValue ?? 0), 0);
  const totalLiabilities = liabilities.reduce((sum, l) => sum + l.attributes.currentBalance, 0);
  const netWorth         = totalAssets - totalLiabilities;

  // Denominator for "% of assets" below — same positive-only basis as
  // buildAllocation()'s total (nothing in the AssetAttributes schema forbids a
  // negative currentValue, e.g. a bad entry, so this can diverge from the raw
  // totalAssets above; keeping both bases in lockstep avoids a "largest holding"
  // reading > 100% or the allocation strip and this list silently disagreeing).
  const positiveAssetsTotal = positions.reduce((sum, p) => sum + Math.max(0, p.attributes.currentValue ?? 0), 0);

  // One account lookup for both consumers below: the largest-holdings context line and
  // the allocation strip's "held in retirement accounts" figure (steward-app#1028).
  const accountById = new Map(accounts.map(a => [a.id, a]));
  const retirement  = retirementHeld(positions, accountById);

  // Largest holdings by current value — surfaces concentration at a glance. An
  // account name (when the asset sits in one) gives context for the value.
  const largestHoldings = [...positions]
    .filter(p => (p.attributes.currentValue ?? 0) > 0)
    .sort((a, b) => (b.attributes.currentValue ?? 0) - (a.attributes.currentValue ?? 0))
    .slice(0, 8);

  // The currency every figure in the hero row is labelled with: the net worth, the
  // assets/liabilities line, the trend chip, the allocation strip's tooltips.
  //
  // Derived from the live holdings those figures are summed from — the SAME function on
  // the SAME four inputs the Portfolio statement uses (steward-app#478). Not "an equivalent
  // rule": one function, because the two screens show the same net worth, and a household
  // whose Dashboard said `$` while their Portfolio said `£` over one number has no way to
  // tell which page is lying.
  //
  // What this line used to be, and why both halves were wrong:
  //
  //  - `netWorthQ.data?.current.attributes.currency` first. The figures beside it are live,
  //    the snapshot is a different source on a different clock, and a household that has
  //    entered holdings but not yet been through a nightly run has no snapshot at all
  //    (steward-app#474 on this second surface). steward-app#484 made the stored value real
  //    data rather than an unconditional 'USD' literal, which is why it survives as the
  //    tail INSIDE `statementCurrency` — but a tail is all it should ever have been.
  //  - `positions[0]?.attributes.currency` behind it. Whatever the API happened to return
  //    first decided the household's headline currency, so two renders of the same data
  //    could disagree. That is the insertion-order dependence #474 exists to remove, and
  //    it is gone: the dominant currency is now decided by the figures themselves.
  //
  // Memoized on the four inputs so a re-render for an unrelated reason (a tour step, the
  // welcome overlay) does not re-rank the holdings.
  const currency = useMemo(
    () => statementCurrency(positions, accounts, liabilities, netWorthQ.data?.current.attributes.currency),
    [positions, accounts, liabilities, netWorthQ.data],
  );
  // `accountsQ` belongs here as of #478, and its absence was a real flash: an empty account
  // is the ONLY evidence of currency in the day-one case this fix exists for, so a sterling
  // family whose /accounts response landed second watched the hero paint `$0` and then flip
  // to `£0`. The gate was already inconsistent about this — an /accounts ERROR blanks the
  // hero (`financialState` below includes it) while /accounts LOADING did not hold it.
  //
  // `netWorthQ` is the FOURTH input to the currency above and is deliberately NOT here, so
  // the gate covers three of four rather than all of them. Leaving it out leaves one narrow
  // flash: a workspace where nothing live names a usable currency (no holdings at all, or
  // every stored code malformed) but a non-USD snapshot exists paints the tail's `$` and
  // then flips when /net-worth lands. Adding it would make every household on every load
  // wait for the snapshot endpoint to see a figure the live queries had already answered —
  // a certain delay for everyone against a flash reachable only with zero live evidence.
  // The amount is right in that window either way; only the symbol moves. Raise the trade
  // if /net-worth ever becomes the fast one.
  const loadingNetWorth  = positionsQ.isLoading || liabilitiesQ.isLoading || accountsQ.isLoading;

  // The money figures above (hero, allocation, largest holdings) all depend on
  // these three fetches. Without this check a failed fetch silently renders as
  // $0 / "No holdings yet" instead of a genuine error — indistinguishable from
  // an empty workspace.
  //
  // `offline` is separated out because it is the same hazard arriving by a
  // different route, and the one most likely to be hit in practice: a paused
  // query reports neither loading nor error, so before this the hero rendered a
  // confident $0 net worth to anyone who lost signal. Telling a family their
  // net worth is zero is the worst sentence this app can produce, so the
  // arithmetic is suppressed entirely rather than shown with a caveat.
  const financialState = queryDisplayState([positionsQ, liabilitiesQ, accountsQ], false);
  const financialOffline = financialState === 'offline';
  const financialError   = financialState === 'error';
  const financialUnavailable = financialOffline || financialError;

  // Whether the allocation widget shares the top row with the hero. Show it while
  // loading (a balanced skeleton pair) and once there's at least one valued asset;
  // otherwise the hero spans the full row instead of leaving a half-empty column.
  const showAllocation = loadingNetWorth || positions.some(p => (p.attributes.currentValue ?? 0) > 0);

  // Trend for the hero: server-computed change deltas + a windowed history
  // series (PS8b). The chip visibility gate stays on the FULL snapshot count
  // (≥2 ever recorded — a real trend, not a single seed point); the sparkline
  // itself only plots the trend chip's own "past month" span, via
  // windowNetWorthHistory — otherwise an old, no-longer-relevant seed-era
  // swing dominates the min/max normalization and a genuinely flat recent
  // trend renders as a dramatic cliff even while the chip reads "+0.0%".
  const historySnapshots = historyQ.data?.items ?? [];
  const nwHistory = windowNetWorthHistory(historySnapshots, '1M');
  // `anchorDate` rides along on the change itself now (steward-app#400), so there's no
  // separate `historyStart` to thread through for captioning — the hero reads it straight
  // off `change`, whether or not a starting point is set.
  const nwChange = historySnapshots.length >= 2 ? (netWorthQ.data?.changes?.['1M'] ?? null) : null;

  // Proactive nudges the app worked out itself — silent when everything is
  // current. Document-expiry items come pre-filtered from the summary
  // endpoint (already narrowed to documents that carry an expiresAt); asset
  // attention (unvalued/stale valuations) is still derived locally since the
  // full asset list is already on hand for the widgets above.
  const attentionDocs: AttentionDocumentSource[] = (summaryQ.data?.attention.documents ?? [])
    .map(d => ({ id: d.id, attributes: { title: d.title, expiresAt: d.expiresAt } }));
  // Broken connections likewise come pre-filtered from the summary endpoint, which
  // already drained the connection list to produce its count — so this costs no extra
  // request. They sort above everything else: see CONNECTION_SEVERITY in lib/attention.
  const attentionConns = summaryQ.data?.attention.connections ?? [];
  // Cash flow (steward-app#2596, docs/39 decision 11): one row while Worth a look has open
  // questions, for a viewer who sees every transaction and can answer. Zero otherwise.
  const cashFlowQuestions = useCashFlowQuestions();
  const attention = (positionsQ.isLoading || summaryQ.isLoading)
    ? []
    : withCashFlowQuestions(attentionItems(positions, attentionDocs, attentionConns), cashFlowQuestions);

  // First-run "getting started" checklist (FR1). Every count but one comes from the
  // summary endpoint (connections/entities/contacts/documents/members/invites); the advisor
  // count is read from the shared `advisor-connections` query — see `firstRunCounts` below.
  //
  // ⚠️ **`enabled` is NARROWED to the audience that can act on the row, and it narrows the
  // factory's own condition rather than restating it** (raised in this branch's independent
  // review). `advisorConnectionsQueryOptions` already refuses to fetch without a token and a
  // resolved workspace; spreading its `enabled` and `&&`-ing keeps that rule in one place, so
  // a future change there still binds here. What this adds is `isAdmin && canWrite`, which is
  // the set of people the row exists for:
  //
  //   - **a non-admin never sees it.** The step is `adminOnly` (`lib/firstRun.ts`), so
  //     `firstRunSteps` filters it out, and the request was never free: it is a full round
  //     trip on the app's most-loaded page, on every mount more than 30s after the last fetch
  //     and again on a tab refocus once that 30s has passed (`staleTime` is 30_000 in
  //     `main.tsx` and `refetchOnWindowFocus` is react-query v5's default, unset here — a
  //     refocus INSIDE the window refetches nothing, so it is not literally every one).
  //
  //     ⚠️ For a non-admin of a family that HAS a connection with a shared folder, it also
  //     logged a `could not reconcile advisor shared-folder ACLs` warn each time: the façade's
  //     `reconcileSharedFolderAcls` calls `listMembers`, which is gated on `mem:manage`, and a
  //     family MEMBER or VIEWER does not hold it. Scoped deliberately — `listMembers` is
  //     skipped when the folder batch is empty, and a family with no active connection returns
  //     before any of it, so this is not "every non-admin load" (checked at source in
  //     `services/steward-app-api/src/handlers/advisor.ts`, 2026-09-10).
  //   - **a read-only member never sees the CARD at all** (the `canWrite` gate on the render
  //     below), so nothing they could read this for is on screen.
  //
  // Deliberately NOT gated on "the checklist is already complete": the advisor count is an
  // INPUT to completeness, so that condition would be circular. Admin-ness and write-ability
  // are facts about the membership, decided before any of these queries run.
  //
  // ⚠️⚠️ A disabled query is not a failed one and not a pending one. `isSuccess` is false for
  // it, so `advisors` stays `undefined` and the row renders unticked — which is invisible to
  // everyone the `enabled` excludes: a non-admin never sees the row (it is `adminOnly`) and a
  // read-only member never sees the card. `isLoading` is false for it too (there is no fetch
  // in flight), which is why the tour gate below does not stall for them. Verified against
  // `@tanstack/react-query@5.101.4`: a disabled query reports `isPending` true but
  // `isFetching` false, and `isLoading` is `isPending && isFetching`. Pinned in
  // `DashboardFirstRunAdvisorStep.test.tsx`.
  const advisorConnectionsOptions = advisorConnectionsQueryOptions(token, activeWorkspaceId);
  const advisorConnectionsQ = useQuery({
    ...advisorConnectionsOptions,
    enabled: advisorConnectionsOptions.enabled && isAdmin && canWrite,
  });
  const firstRunCounts: FirstRunCounts = {
    connections: counts?.connections ?? 0,
    entities: counts?.entities ?? 0,
    contacts: counts?.contacts ?? 0,
    documents: counts?.documents ?? 0,
    members: counts?.members ?? 0,
    invites: counts?.invites ?? 0,
    // "Connect your advisor" (steward-app#1343 §Surfaces item 5). The ONE count here that
    // does not come from the summary endpoint, because that endpoint has no advisor field.
    //
    // ⚠️ Built from `advisorConnectionsQueryOptions`, the SHARED factory for the
    // `advisor-connections` key that the Documents pillar and People → Advisors already
    // observe, so this joins an existing cache entry rather than declaring a second shape of
    // it — the steward-app#671 drift class.
    //
    // ⚠️⚠️ **THE COST, PRICED HONESTLY, because this is the app's most-loaded page** (raised
    // in this branch's code review, which is right that the first version of this comment
    // under-priced it). For a family with NO advisor it is one `listAllEntities` and no more:
    // `listConnections` returns early on an empty active set. For a CONNECTED family it is
    // more than a read — `reconcileSharedFolderAcls` runs BEFORE that early return
    // (`handlers/advisor.ts`), costing one `listMembers` plus one `listAcl` per active
    // connection that has a shared folder, and it is a repair that can WRITE. It never throws
    // and writes nothing in the steady state, and Documents and People → Advisors already
    // trigger it, but until now the Dashboard did not, so a connected family's session start
    // now runs it where it previously did not.
    //
    // That cost is now paid only by admins who can write (see the `enabled` above). What is
    // NOT gated on is whether the row would already be complete, because the checklist has to
    // be able to tell "no advisor" from "we could not read", which is exactly what the
    // `isSuccess` branch below is for.
    //
    // ⚠️⚠️ **`undefined` means the count is UNKNOWN, and `firstRunSteps` renders the row
    // anyway, unticked** (see `FirstRunCounts.advisors`, which owns that rule). It used to
    // mean "omit the row", and this branch changed it: an appearing row is what moved the
    // denominator after first paint, so the row is now present from the first render and the
    // only later movement is it ticking.
    //
    // ⚠️ So `undefined` and `0` produce the SAME row today, and this expression keeps them
    // apart anyway. Not because anything branches on it: because the decision about what
    // "unknown" means belongs in `firstRunSteps` alone, and a caller that has already
    // collapsed unknown into zero cannot have that decision changed later without changing
    // the caller too. Said plainly so the next reader does not go looking for the branch.
    //
    // ⚠️⚠️ **`useConnectedFirms` was the obvious hook and is still the wrong one, for a
    // reason that OUTLIVED the model change.** Its answer (`[]` for a refusal, a pause and a
    // request in flight alike) now happens to render the same unticked row this does, so that
    // is no longer the argument. What it does not do is carry the `enabled` narrowing above:
    // it subscribes `advisorConnectionsQueryOptions` unconditionally, which is exactly the
    // second defect this branch fixed, and it would re-fire the read for every non-admin on
    // the app's most-loaded page.
    //
    // ⚠️⚠️ **A FAILED read and a PENDING one both leave the row unticked, and NEITHER holds
    // the card back.** That is deliberate and it is a trade: while the read is in flight (or
    // after it failed) an admin family who HAS an advisor reads "Connect your advisor" as
    // undone, which is briefly false. The alternative, which this branch shipped first and
    // then removed, was to hold the card until the read settled — and that is worse in two
    // measured ways, both in `DashboardFirstRunAdvisorStep.test.tsx`: the tour waits ~3s
    // (`TourProvider.tsx`, `MAX_TRIES = 30` at 100ms) and then opens on whatever is present,
    // and `tour/steps.ts` passes a step whose anchor is absent and never returns to it, so a
    // cold `/advisor/connections` could skip the getting-started stop for the whole session;
    // and a FAILING read holds the card for ~31s (`REQUEST_TIMEOUT_MS` 15s, `retry: 1`) even
    // though all five summary-derived steps have their data. A row that unticks itself once
    // is the milder error than a card that is not there when the tour walks past.
    advisors: advisorConnectionsQ.isSuccess
      ? (advisorConnectionsQ.data ?? []).filter((c: AdvisorConnection) => c.attributes.status === 'active').length
      : undefined,
  };
  // Ready only once the queries the steps depend on have settled successfully —
  // on error the card stays hidden (never blocks the app, never shows false state).
  // Also gate on the profile being loaded: the dismissed flag lives on it, so
  // showing before it resolves flashes the card for users who've dismissed it
  // (the count queries can win the race against /me on a hard refresh). The
  // summary endpoint itself resolves the admin/non-admin member+invite split
  // server-side (invites is 0 for a non-admin caller), so there's no separate
  // admin-only readiness gate to wait on anymore.
  //
  // ⚠️ **The advisor query is deliberately NOT part of this gate, in any form.** It
  // contributes a STEP, and an earlier cut of this branch therefore waited on
  // `!advisorConnectionsQ.isLoading` here so the denominator could not tick up. That trade is
  // the wrong way round: the card is the getting-started tour anchor, the tour waits about
  // three seconds and then opens on whatever is on screen, and a step whose anchor is absent
  // at that moment is passed and never returned to (`tour/steps.ts`). A cold advisor read can
  // plausibly exceed that (a Lambda start, plus `reconcileSharedFolderAcls` running to
  // completion before `listConnections` returns), and a FAILING one keeps the card off screen
  // for about 31 seconds (`REQUEST_TIMEOUT_MS` is 15_000 in `api/client.ts`, `retry: 1` in
  // `main.tsx`) even though every step the summary feeds is ready. Both outcomes are the very
  // #510 class the wait was meant to serve.
  //
  // The tick-up is instead prevented at the source: `firstRunSteps` includes the advisor row
  // from the first render, unticked until the read says otherwise, so the denominator is at
  // its final value immediately and the only later movement is a row ticking. `isSuccess`
  // would have been wrong here for two further reasons that still stand — it is false for a
  // FAILED read, which would hide the whole card over a step the other five do not depend on,
  // and false for a DISABLED one, which since the `enabled` narrowing above is the ordinary
  // state for every non-admin and every read-only member.
  const coreReady = !!profile && summaryQ.isSuccess;
  const dismissed = !!activeWorkspaceId && (profile?.preferences?.firstRunDismissed?.includes(activeWorkspaceId) ?? false);
  const firstRun = useFirstRun({
    counts: firstRunCounts,
    isAdmin,
    workspaceId: activeWorkspaceId,
    ready: coreReady,
    dismissed,
    onDismiss: () => { if (activeWorkspaceId) void setFirstRunDismissed(activeWorkspaceId, true); },
  });

  // Re-entry (FR4): the account menu links here with ?onboarding=1 — un-dismiss
  // for this session, scroll the card into view, then strip the param so a reload
  // doesn't force it open again.
  const reopenFirstRun = firstRun.reopen;
  useEffect(() => {
    if (searchParams.get('onboarding') !== '1') return;
    reopenFirstRun();
    firstRunRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    const next = new URLSearchParams(searchParams);
    next.delete('onboarding');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, reopenFirstRun]);

  // The checklist is the only tour anchor whose existence is still an open
  // question while this page loads, and the tour's forward walk never revisits
  // a step it passed — so a tour that opened mid-flight skipped the checklist
  // for good, and its "N of M" denominator ticked up mid-walk (#510). Hold the
  // tour's opening (src/tour/TourProvider.tsx) until the answer is known.
  //
  // `isLoading`, not `isPending`: a paused (offline) query is not on its way,
  // and holding for one would stall the tour for the whole poll window to no
  // purpose — see lib/queryState.ts on that distinction. Everything else the
  // gate reads is settled by now or settled forever: /me has already resolved
  // (AssuranceFloorGate holds the whole shell until it does, so this page
  // cannot render before it), and a failed summary, a dismissed or complete
  // list, and a read-only member are all answers, not waits.
  //
  // ⚠️ **The advisor query is deliberately NOT a wait here**, and an earlier cut
  // of this branch had it in this `||`. Holding the tour on it is self-defeating:
  // the hold only buys ~3 seconds (`TourProvider.tsx`, `MAX_TRIES = 30` at 100ms),
  // after which the tour opens on whatever is present and passes the anchor for
  // good, and for a connected family the advisor endpoint does strictly more work
  // before it answers (`reconcileSharedFolderAcls` runs to completion before
  // `listConnections` returns, `handlers/advisor.ts`). So the case the hold was
  // for — a slow advisor read — is the case where the hold most plausibly runs
  // out and skips the checklist stop entirely, and a failed read holds for the
  // full ~31s timeout window besides. The tick-up it was fixing is now fixed in
  // `firstRunSteps`, which carries the advisor row from the first render.
  //
  // The net-worth hero is conditional too, but not *undecided*: it renders as
  // a skeleton throughout the load and only disappears once its fetches have
  // settled into offline/error, so it needs no claim of its own.
  const firstRunPending = summaryQ.isLoading;

  const greetName = greetingName(profile);

  return (
    <div>
      <WelcomeOverlay />
      <NameNudge />
      <AdvisorLandingNudge />

      <div ref={firstRunRef} data-tour-pending={firstRunPending ? 'getting-started' : undefined}>
        {/* Every checklist row is a setup task (connect an account, upload a document,
            invite someone). A read-only member can do none of them, so the card would
            be a list of dead ends rather than a way in. */}
        {firstRun.show && canWrite && (
          <GettingStartedCard
            steps={firstRun.steps}
            done={firstRun.done}
            total={firstRun.total}
            isComplete={firstRun.isComplete}
            onDismiss={firstRun.dismiss}
          />
        )}
      </div>

      {/* Greeting — deliberately compact (steward-app#584). A beta user asked for their net
          worth to be the first thing on this page, and the two things above it were this row
          and the Ask launcher that #583 removed. The sentence itself survives intact: it is
          the app's one person-to-person moment, and it stays the page's only `h1`. What went
          is the size (28px → 22px) and the margin below the row.

          The suite lockup stays at every width, which is a reversal inside this same change:
          it was briefly `lg:hidden`, on the reasoning that above `lg` the sidebar's brand
          lockup says the same thing anyway. That premise is false exactly when it matters —
          a railed sidebar renders `shortName.charAt(0)` and nothing else (Layout.tsx), and
          docking the assistant panel rails it — so a white-label firm's name would have been
          on screen nowhere on this page. Worth ~14px to not lose someone else's brand. */}
      <div className="flex items-end justify-between mb-6">
        <div>
          <h1 className="font-serif text-[22px] font-normal text-navy tracking-[-0.01em] mb-0.5">
            {greeting()}{greetName ? `, ${greetName}` : ''}.
          </h1>
          <div className="text-[11px] font-semibold text-slate uppercase tracking-[0.12em] font-sans">
            {suiteName}
          </div>
        </div>
        <div className="text-[11px] text-slate font-sans font-medium text-right">
          {todayStr()}
        </div>
      </div>

      {/* Headline row: net worth hero + asset-allocation widget, side by side on
          desktop (stacked on smaller). When there are no valued assets the hero
          spans the full row and the allocation widget is omitted. If any of the
          underlying fetches failed, show a genuine error instead of a misleading
          $0 hero. */}
      {financialUnavailable ? (
        <div className="mb-10">
          {financialOffline ? (
            <OfflineState
              message="You're offline, so your net worth and holdings can't load right now."
              onRetry={() => { void positionsQ.refetch(); void liabilitiesQ.refetch(); void accountsQ.refetch(); }}
            />
          ) : (
            <QueryErrorState
              message="We couldn't load your net worth and holdings."
              onRetry={() => { void positionsQ.refetch(); void liabilitiesQ.refetch(); void accountsQ.refetch(); }}
            />
          )}
        </div>
      ) : (
        // Both cards stretch to the row's height (steward-app#2827) only when the Net Worth card
        // has a sparkline to grow into the space. Without one (a new workspace, fewer than two
        // snapshots) stretching leaves the gap #2509 set items-start to avoid, so it stays start.
        <div data-testid="overview-headline-row" className={`mb-10 grid grid-cols-1 ${nwHistory.length >= 2 ? 'items-stretch' : 'items-start'} gap-5 ${showAllocation ? 'lg:grid-cols-2' : ''}`}>
          <NetWorthHero
            netWorth={netWorth}
            totalAssets={totalAssets}
            totalLiabilities={totalLiabilities}
            currency={currency}
            loading={loadingNetWorth}
            change={nwChange}
            history={nwHistory}
            range="1M"
            to="/portfolio"
          />
          {showAllocation && (
            <AllocationStrip
              assets={positions}
              retirement={retirement}
              currency={currency}
              loading={loadingNetWorth}
              theme={theme}
              to="/portfolio/assets"
            />
          )}
        </div>
      )}

      {/* Net cash flow (steward-app#2596): full width under the headline row. Renders nothing for
          anyone without a cash flow level, a firm advisor, or a family with cash flow off. */}
      <NetCashFlowCard />

      {/* A firm's open onboarding request (steward-app#1427). Below the headline row on
          purpose: #584 put net worth first at a beta user's request, and a request that has
          been open for days does not need to push it down. Above "Needs attention", because
          this one is a person asking, not something the app noticed. Renders nothing for
          anyone who is not family, and nothing while every request is answered or put off. */}
      <OnboardingRequestCard />

      {/* A firm's onboarding form or requests, for someone with a workspace already (#2465). */}
      <OnboardingInvitationCard />

      {/* Needs attention — only rendered when the app has found something */}
      <NeedsAttentionList items={attention.slice(0, 6)} more={Math.max(0, attention.length - 6)} />

      {/* Largest holdings — top assets by value (concentration at a glance). Full
          width in a two-up grid; Recent documents was dropped as the least
          decision-useful surface (the Documents pillar + assistant cover it).
          Hidden whenever the financial fetches are unavailable (offline or
          errored) — the state above already covers it, so there's no need for a
          second box saying the same thing. */}
      {!financialUnavailable && (
      <div>
        <div className="flex items-center justify-between mb-4">
          <div className="font-serif text-base font-normal text-navy">Largest holdings</div>
          <button
            onClick={() => navigate('/portfolio/assets')}
            className="text-[10px] font-semibold text-slate bg-transparent border-none cursor-pointer uppercase tracking-[0.1em] font-sans p-0"
          >
            View all
          </button>
        </div>
        {loadingNetWorth ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-2.5">
            <div className="bg-surface border border-line rounded-token px-5 py-4 space-y-2.5">
              <div className="skeleton h-4 w-2/5" />
              <div className="skeleton h-3 w-3/5" />
            </div>
            <div className="bg-surface border border-line rounded-token px-5 py-4 space-y-2.5">
              <div className="skeleton h-4 w-2/5" />
              <div className="skeleton h-3 w-3/5" />
            </div>
          </div>
        ) : largestHoldings.length === 0 ? (
          <div className="bg-surface border border-line rounded-token p-6 text-slate text-[13px] font-sans">
            No holdings yet.
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-2.5">
            {largestHoldings.map(h => {
              const a = h.attributes;
              const value = a.currentValue ?? 0;
              const pct = positiveAssetsTotal > 0 ? (value / positiveAssetsTotal) * 100 : 0;
              const context = a.accountId ? accountById.get(a.accountId)?.attributes.name : undefined;
              return (
                <div
                  key={h.id}
                  onClick={() => navigate(`/portfolio/assets/asset/${h.id}`)}
                  className="bg-surface border border-line rounded-token px-5 py-4 cursor-pointer flex items-center justify-between gap-4 shadow-panel transition-[box-shadow,border-color] duration-150 hover:border-bronze/35 hover:shadow-panel-hover"
                >
                  <div className="min-w-0">
                    <div className="font-sans font-semibold text-[13px] text-navy mb-0.5 truncate"><UserText>{a.name}</UserText></div>
                    <div className="text-[10px] font-semibold text-slate uppercase tracking-[0.08em] font-sans truncate">
                      {assetGroupFor(a.assetClass)}{context ? <> · <UserText>{context}</UserText></> : ''}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-serif text-lg font-normal text-navy">
                      {formatCurrency(value, currencyCode(a.currency) ?? currency, 0)}
                    </div>
                    <div className="text-[10px] font-sans tabular-nums text-slate">{pct.toFixed(0)}% of assets</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      )}
    </div>
  );
}
